import AddLinkIcon from "@mui/icons-material/AddLink";
import LanguageIcon from "@mui/icons-material/Language";
import LinkOffOutlinedIcon from "@mui/icons-material/LinkOffOutlined";
import { Chip, Link, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { TextInput } from "components/TextInput";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Control, Controller, useFieldArray } from "react-hook-form";
import { v4 } from "uuid";

import { TaskFormType } from "./types";

type LinksControlProps = {
  control: Control<TaskFormType>;
};

// Like your browser, after you hit enter, we will prepend the protocol to the URL
// Explicitly ignoring other protocols (don't give me your FTP URL)
const ensureHttps = (url: string) => (url.startsWith("http") ? url : `https://${url}`);

export const LinksControl: React.FC<LinksControlProps> = ({ control }) => {
  const theme = useTheme();

  const [isAdding, setIsAdding] = useState(false);
  const [newLink, setNewLink] = useState("");
  const newLinkInputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  const {
    fields: links,
    append,
    remove,
  } = useFieldArray({
    control,
    name: "links",
    keyName: "_id",
  });

  useEffect(() => {
    if (isAdding) {
      newLinkInputRef.current?.focus();
    }
  }, [isAdding]);

  const handleAddLink = useCallback(() => {
    const url = newLink.trim();
    if (!url) {
      setIsAdding(false);
      setNewLink("");
      return;
    }
    append({ url: ensureHttps(url), id: v4() });
    setNewLink("");
    newLinkInputRef.current?.focus();
  }, [append, newLink]);

  return (
    <Stack direction="row" gap={theme.spacing(1)} flexWrap="wrap">
      {links.map((link, index) => (
        <Controller
          key={link.id}
          control={control}
          name={`links.${index}.url`}
          render={({ fieldState: { error } }) => (
            <Stack>
              <Chip
                size="small"
                sx={{
                  backgroundColor: error ? "#FFF" : `${theme.palette.primary.main}20`,
                  color: error ? theme.palette.text.secondary : theme.palette.primary.main,
                  border: error ? `1px solid ${theme.palette.error.main}` : "none",
                }}
                icon={
                  error ? (
                    <LinkOffOutlinedIcon fontSize="small" color="disabled" />
                  ) : (
                    <LanguageIcon fontSize="small" color="disabled" />
                  )
                }
                label={
                  error ? (
                    <Typography variant="body2">{link.url}</Typography>
                  ) : (
                    <Link
                      href={ensureHttps(link.url ?? "")}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                      variant="body2"
                    >
                      {new URL(link.url).hostname}
                    </Link>
                  )
                }
                onDelete={() => remove(index)}
              />
              {error && (
                <Typography variant="caption" color={theme.palette.error.main}>
                  {error.message}
                </Typography>
              )}
            </Stack>
          )}
        />
      ))}
      {isAdding ? (
        <Chip
          size="small"
          label={
            <TextInput
              inputRef={newLinkInputRef}
              value={newLink}
              onChange={(e) => setNewLink(e.target.value)}
              onBlur={handleAddLink}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddLink();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  setIsAdding(false);
                  setNewLink("");
                }
              }}
              size="small"
              placeholder="add link"
              sx={{ minWidth: 80 }}
            />
          }
        />
      ) : links.length === 0 ? (
        <Chip
          size="small"
          label="add link"
          onClick={() => setIsAdding(true)}
          icon={<AddLinkIcon fontSize="small" />}
        />
      ) : (
        <Tooltip title="Add link" arrow>
          <Chip
            size="small"
            onClick={() => setIsAdding(true)}
            icon={<AddLinkIcon fontSize="small" />}
            slotProps={{
              root: { sx: { px: 0.5 } },
              label: { sx: { pl: 0 } },
            }}
          />
        </Tooltip>
      )}
    </Stack>
  );
};
